import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {
    BIG_WIN_LEVEL_NAMES,
    IBigWinLevel,
    IFeatureData,
    IGameData,
    IInitData,
    IMonster,
    IMonsterWalk,
    IPosition,
    IXNudgeData
} from "./IData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {Game} from "../Game";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {GameModeNames} from "./GameModeNames";

export class Model extends GameModel<IInitData, IGameData> {

    public isRestore: boolean = false;
    public winRatio: number;
    public xSplitData: IFeatureData[];
    public xNudgeData: IXNudgeData[];
    public xBombData: IFeatureData[];
    public gEData: IFeatureData[];
    public bulldozerLastIdx: number;
    public bulldozerExplodePosition: IMonsterWalk;
    public monstersWithNoLife: number[];
    public isBigWin: boolean = false;
    public isNormalWin: boolean = false;
    public buyFeatureName: string;
    public canBulldozerDrive: boolean;
    public monsterIdxWithBZ: number;
    private _canMonstersWalk: boolean;
    public isMayHemAlreadyTriggered: boolean = false;

    constructor(api: ApiPlugin) {
        super(api, true);
        api.events.once("featureName", (data: string) => {
            this.buyFeatureName = data;
        });
        Game.apiPlugin.events.on(APIEvent.BET, (betData: APIFeatureBetEventData) => {
            for (let index: number = 0; index < Game.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Game.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    Logger.logDev("Opted Feature : ", this.buyFeatureName);
                    break;
                }
            }
        });

        Game.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, (betData: APIFeatureBetEventData) => {
            for (let index: number = 0; index < Game.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Game.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    break;
                }
            }
        });
    }

    public onGame(data: IGameData): void {
        super.onGame(data);
        // #27
        const mxCornerAtStart: IMonsterWalk = data.mxLeftBottomCornerAtStart;
        if (mxCornerAtStart && !Game.mxSymbol) {
            if (mxCornerAtStart.x < 0) {
                Game.mxVSymbolReelId = (2 + mxCornerAtStart.x);
                data.reels[Game.mxVSymbolReelId][mxCornerAtStart.y] = "MX_V_R";
            } else {
                Game.mxVSymbolReelId = (mxCornerAtStart.x);
                data.reels[Game.mxVSymbolReelId][mxCornerAtStart.y] = "MX_V_L";
            }
        }

        const reels: string[][] = JSON.parse(JSON.stringify(data.reels));
        this.isRestore = false;

        this._canMonstersWalk = false;
        for (let i: number = 0; i < data.monsters.length; i++) {
            if (data.monsters[i].path && data.monsters[i].path.length > 1) {
                this._canMonstersWalk = true;

                // if any one is true, it's enough to set this data
                break;
            }
        }

        this.winRatio = (data.spinWin / data.playedBetValue);
        this.xSplitData = this.prepareXSplitData(reels);
        this.xNudgeData = this.prepareXNudgeData(reels);
        this.canBulldozerDrive = (data.bulldozerPath && data.bulldozerPath.length > 0);
        this.monsterIdxWithBZ = -1;
        this.modifyMonstersData(data.monsters, data.evaluatedArea, data.bulldozerPath);
        this.xBombData = this.prepareXBombNGEData(reels, "XB");
        this.gEData = this.prepareXBombNGEData(reels, "GE");
        this.bulldozerLastIdx = (data.bulldozerPath && data.bulldozerPath.length ? this.getBulldozerLastIdx(data.bulldozerPath) : -1);
        this.bulldozerExplodePosition = this.getBulldozerExplodePosition(data.bulldozerPath, data.monsters, data.carriedSymbolUserNode);
        this.monstersWithNoLife = this.prepareMonstersWithNoLifeData(data.monsters);

        const isRoundEnding: boolean = (this.data.mode.includes("AVALANCHE") && !this.data.nextMode.includes("AVALANCHE"));
        this.isNormalWin = (this.winRatio > 0 && this.winRatio < 15 && isRoundEnding);
        this.isBigWin = this.isBigWinAsPerFeature(isRoundEnding); //#125 big win thresholds as per buy feature
    }

    public canXBombTrigger(): boolean {
        return !((Game.model.bulldozerExplodePosition.x !== -1) ||
            (Game.model.gameData.grenadePosition && Game.model.gameData.grenadePosition.x !== -1) ||
            (Game.model.gameData.bazookaPosition && Game.model.gameData.bazookaPosition.x !== -1) ||
            (Game.model.gameData.airStrikeRocketCoordinates && Game.model.gameData.airStrikeRocketCoordinates.length > 0) ||
            (Game.isMonsterMove())) // #144
    }

    public getBigWinLevels(): IBigWinLevel[] {
        let levels: IBigWinLevel[] = [];
        const ratio: number = this.winRatio,
            multiplier: number = 1;

        if (ratio >= (15 * multiplier)) {
            levels.push({
                from: 0,
                to: 25 * multiplier,
                duration: 4,
                id: 1,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_01
            });
        }

        if (ratio >= (25 * multiplier)) {
            levels.push({
                from: (25 * multiplier),
                to: 50 * multiplier,
                duration: 4,
                id: 2,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_02
            });
        }

        if (ratio >= (50 * multiplier)) {
            levels.push({
                from: 50 * multiplier,
                to: 100 * multiplier,
                duration: 5,
                id: 3,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_03
            });
        }

        if (ratio >= (100 * multiplier)) {
            levels.push({
                from: 100 * multiplier,
                to: 250 * multiplier,
                duration: 6,
                id: 4,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_04
            });
        }

        if (ratio >= (250 * multiplier)) {
            levels.push({
                from: 250 * multiplier,
                to: (Number.MAX_VALUE),
                duration: 6,
                id: 5,
                name: BIG_WIN_LEVEL_NAMES.BIG_WIN_05
            });
        }

        levels = this.setLastConfig(ratio, levels);

        return levels;
    }

    protected onInit(data: IInitData): void {
        super.onInit(data);
        this.isRestore = data.isRestoreState;
        if (data.nextPlayerInteraction?.featureName != undefined) {
            this.buyFeatureName = data.nextPlayerInteraction.featureName;
        }

        if (data.monsters) {
            data.monsters.forEach((monster: IMonster) => {
                let flagIdx = 0;
                for (let carriedSym of monster.carriedSymbols) {
                    flagIdx++;
                    Game.monsterCarriedSymbols[monster.symbol - Game.MONSTER_SYMBOLS.length].push(carriedSym.toLowerCase() + "_" + flagIdx);
                }
            });
        }

        if (this.isRestore) {
            this.isMayHemAlreadyTriggered = data.mayhemSpin;
            const mxCornerAtStart: IMonsterWalk = data.mxLeftBottomCorner;
            if (mxCornerAtStart && !Game.mxSymbol) {
                if (mxCornerAtStart.x < 0) {
                    Game.mxVSymbolReelId = (2 + mxCornerAtStart.x);
                    data.reelsAfterAvalanche[Game.mxVSymbolReelId][mxCornerAtStart.y] = "MX_V_R";
                } else {
                    Game.mxVSymbolReelId = (mxCornerAtStart.x);
                    data.reelsAfterAvalanche[Game.mxVSymbolReelId][mxCornerAtStart.y] = "MX_V_L";
                }
            }
        }
    }

    private isBigWinAsPerFeature(isRoundEnding: boolean): boolean {
        let isBigWin: boolean = false;

        if (isRoundEnding) {
            if (this.buyFeatureName === "GOD_MODE") {
                if (this.winRatio >= 5000) {
                    isBigWin = true;
                }
            } else if (this.buyFeatureName === "BOOSTED_BET_ITEMS") {
                if (this.winRatio >= 40) {
                    isBigWin = true;
                }
            } else if (this.buyFeatureName === "FREESPIN_3_SCATTERS") {
                if (this.winRatio >= 50) {
                    isBigWin = true;
                }
            } else if (this.buyFeatureName === "FREESPIN_4_SCATTERS") {
                if (this.winRatio >= 150) {
                    isBigWin = true;
                }
            } else if (this.buyFeatureName === "FREESPIN_MAYHEM") {
                if (this.winRatio >= 350) {
                    isBigWin = true;
                }
            } else if (this.buyFeatureName === "LUCKY_DRAW") {
                if (this.winRatio >= 100) {
                    isBigWin = true;
                }
            } else if (this.winRatio >= 15) {
                isBigWin = true;
            }

            // if it's not big win, it should be normal win
            if (this.winRatio > 0) {
                this.isNormalWin = !isBigWin;
            }
        }

        return isBigWin;
    }

    private prepareXSplitData(reels: string[][]): IFeatureData[] {
        const retVal: IFeatureData[] = [];

        for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
            for (let rowId: number = 0; rowId < Game.ACTIVE_ROWS_NUM; rowId++) {
                if (reels[reelId][rowId] === "XS") {
                    retVal.push({
                        reelId: reelId,
                        rowId: rowId
                    });
                }
            }
        }

        return retVal;
    }

    private prepareXNudgeData(reels: string[][]): IXNudgeData[] {
        const retVal: IXNudgeData[] = [];

        for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
            for (let rowId: number = 0; rowId < Game.ACTIVE_ROWS_NUM; rowId++) {
                if (reels[reelId][rowId] === "XN") {
                    retVal.push({
                        reelId: reelId,
                        rowId: rowId,
                        nudgeDownwardsRowId: this.getNudgeDownwardsRowIdForXNudge(rowId, reels[reelId])
                    });

                    break;
                }
            }
        }

        return retVal;
    }

    private getNudgeDownwardsRowIdForXNudge(xNRowId: number, reelSymbols: string[]): number {
        for (let nudgeDownwardsRowId: number = (xNRowId + 1); nudgeDownwardsRowId < Game.ACTIVE_ROWS_NUM; nudgeDownwardsRowId++) {
            if (nudgeDownwardsRowId === (Game.ACTIVE_ROWS_NUM - 1)) {
                if ((reelSymbols[nudgeDownwardsRowId] === "CR") || !(Game.PAYOUT_SYMBOLS.includes(reelSymbols[nudgeDownwardsRowId]))) {
                    return (nudgeDownwardsRowId - 1);
                }
                return nudgeDownwardsRowId;
            }

            if (Game.PAYOUT_SYMBOLS.includes(reelSymbols[nudgeDownwardsRowId])) {
                continue;
            }

            return (nudgeDownwardsRowId - 1);
        }

        return xNRowId;
    }

    private modifyMonstersData(monsterData: IMonster[], evalArea: string[][], bzPath: IMonsterWalk[]): void {
        if (this.canBulldozerDrive) {
            let monsterIdxWithBulldozer: number = 0;
            if (!(bzPath[0].x === monsterData[monsterIdxWithBulldozer].path[0].x && bzPath[0].y === monsterData[monsterIdxWithBulldozer].path[0].y)) {
                for (let i: number = 1; i < monsterData.length; i++) {
                    if ((bzPath[0].x === monsterData[i].path[0].x && bzPath[0].y === monsterData[i].path[0].y)) {
                        monsterIdxWithBulldozer = i;

                        break;
                    }
                }
            }

            monsterData[monsterIdxWithBulldozer].path.splice(0, bzPath.length - 1);
            this.monsterIdxWithBZ = monsterIdxWithBulldozer;
        }

        if (this._canMonstersWalk) {
            const monstersThatWillWalk: number[] = [];
            monsterData.forEach((monster: IMonster, idx: number) => {
                if (monster.path && monster.path.length > 1) {
                    monstersThatWillWalk.push(idx);
                }
            });

            if (monstersThatWillWalk.length > 1) {
                const wildIds: number[] = [];
                for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                    for (let rowId: number = 0; rowId < Game.ACTIVE_ROWS_NUM; rowId++) {
                        if (evalArea[reelId][rowId] === "W") {
                            wildIds.push(((reelId * Game.TOTAL_REELS_NUM) + rowId));
                        }
                    }
                }

                for (let ctr: number = 0; ctr < monstersThatWillWalk.length - 1; ctr++) {
                    const path: IMonsterWalk[] = monsterData[monstersThatWillWalk[ctr]].path;

                    path.forEach((val: IMonsterWalk) => {
                        val.canRemove = true;
                        if (evalArea[val.x][val.y] === "W") {
                            for (let i: number = (ctr + 1); i < monstersThatWillWalk.length; i++) {
                                const otherPath: IMonsterWalk[] = monsterData[monstersThatWillWalk[i]].path;
                                for (let j: number = 0; j < otherPath.length; j++) {
                                    if (val.x === otherPath[j].x && val.y === otherPath[j].y) {
                                        val.canRemove = false;

                                        break;
                                    }
                                }
                                if (!val.canRemove) {
                                    break;
                                }
                            }
                        }
                    });

                    monsterData[monstersThatWillWalk[ctr]].path = path;
                }
                monsterData[monstersThatWillWalk[monstersThatWillWalk.length - 1]].path.forEach((val: IMonsterWalk) => {
                    val.canRemove = true;
                });
            } else {
                monsterData[monstersThatWillWalk[0]].path.forEach((val: IMonsterWalk) => {
                    val.canRemove = true;
                });
            }
        }

        // #329
        if (this.gameData.mode === GameModeNames.NORMAL && this.buyFeatureName === "BOOSTED_BET_ITEMS") { // Tank Booster's case -> it can happen that in tank booster xBet that weapon is executed in that same response and that weapon isn't sent in the carriedSymbols array.
            const carriedNode: IPosition = this.gameData.carriedSymbolUserNode;

            if (carriedNode && carriedNode.x !== -1 && carriedNode.y !== -1) {
                if (this.canBulldozerDrive) {
                    // #490, #494
                    let weaponsInBulldozerPath: string[] = [];
                    for (let i: number = 0; i < bzPath.length; i++) {
                        if (Game.BOOSTER_SYMBOLS.includes(evalArea[bzPath[i].x][bzPath[i].y])) {
                            weaponsInBulldozerPath.push(evalArea[bzPath[i].x][bzPath[i].y]);
                        }
                    }

                    // #490, #494
                    if (weaponsInBulldozerPath.length) {
                        const carriedSymbols: string[] = monsterData[this.monsterIdxWithBZ].carriedSymbols;
                        weaponsInBulldozerPath.forEach((weapon: string) => {
                            const wpIdx: number = carriedSymbols.indexOf(weapon);
                            if (wpIdx !== -1) {
                                carriedSymbols.splice(wpIdx, 1);
                            }
                        });
                        monsterData[this.monsterIdxWithBZ].carriedSymbols = carriedSymbols.concat("BU");
                    } else {
                        monsterData[this.monsterIdxWithBZ].carriedSymbols = monsterData[this.monsterIdxWithBZ].carriedSymbols.concat("BU");
                    }
                } else {
                    for (let i: number = 0; i < monsterData.length; i++) {
                        const monster: IMonster = monsterData[i];

                        if (monster.path && monster.path[0].x === carriedNode.x && monster.path[0].y === carriedNode.y) {
                            let weaponName: string = "AS";
                            if (this.gameData.grenadePosition && this.gameData.grenadePosition.x !== -1) {
                                weaponName = "GR";
                            } else if (this.gameData.bazookaPosition && this.gameData.bazookaPosition.x !== -1) {
                                weaponName = "BZ";
                            }

                            monster.carriedSymbols = monster.carriedSymbols.concat(weaponName);

                            break;
                        }
                    }
                }
            }

            // #474
            const reels: string[][] = this.gameData.reels;
            monsterData.forEach((monster: IMonster) => {
                const path: IMonsterWalk[] = monster.path;
                if (path && path.length > 0) {
                    const visitedIdx: string[] = [];
                    path.forEach((val: IMonsterWalk) => {
                        if (!visitedIdx.includes(val.x + "_" + val.y)) {
                            const itemName: string = reels[val.x][val.y];
                            if (Game.BOOSTER_SYMBOLS.includes(itemName)) {
                                monster.carriedSymbols.splice(monster.carriedSymbols.lastIndexOf(itemName), 1);
                            }
                            visitedIdx.push(val.x + "_" + val.y);
                        }
                    });
                }
            });
        }
    }

    private prepareXBombNGEData(reels: string[][], featureName: string): IFeatureData[] {
        const retVal: IFeatureData[] = [];

        for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
            for (let rowId: number = Game.ACTIVE_ROWS_NUM - 1; rowId >= 0; rowId--) {
                if (reels[reelId][rowId] === featureName) {
                    retVal.push({
                        reelId: reelId,
                        rowId: rowId
                    });
                }
            }
        }

        return retVal;
    }

    private getBulldozerLastIdx(path: IMonsterWalk[]): number {
        return ((path[path.length - 1].x * Game.TOTAL_REELS_NUM) + path[path.length - 1].y);
    }

    private getBulldozerExplodePosition(path: IMonsterWalk[], monsters: IMonster[], carriedSymbolUserNode: IPosition): IMonsterWalk {
        if (path && path.length === 0) {
            if (carriedSymbolUserNode && carriedSymbolUserNode.x !== -1) {
                for (let i: number = 0; i < monsters.length; i++) {
                    if (monsters[i].path && monsters[i].path.length > 0) {
                        const reelId: number = monsters[i].path[0].x,
                            rowId: number = monsters[i].path[0].y;

                        // #480
                        if (carriedSymbolUserNode.x === reelId && carriedSymbolUserNode.y === rowId) {
                            return {
                                x: reelId,
                                y: rowId,
                                canRemove: true
                            };
                        }
                    }
                }
            }
        }

        return {
            x: -1,
            y: -1,
            canRemove: true
        };
    }

    private prepareMonstersWithNoLifeData(monsterData: IMonster[]): number[] {
        const retVal: number[] = [];

        monsterData.forEach((monster: IMonster) => {
            if (monster.lifeLeft === 0) {
                retVal.push(monster.symbol - Game.MONSTER_SYMBOLS.length);
            }
        });

        return retVal;
    }

    private setLastConfig(winRatio: number, configs: IBigWinLevel[]): IBigWinLevel[] {
        const lastConfig: IBigWinLevel = configs[configs.length - 1];

        if (lastConfig) {
            if (lastConfig.from * 1.1 > winRatio && configs.length > 1) {
                configs.pop();
                return this.setLastConfig(winRatio, configs);
            }
            lastConfig.to = winRatio;
        }

        return configs;
    }
}